/**
 * tutorPreCallViewModel — ce que le précall du masteur dit de sa séance, et ce que son entrée mesure.
 *
 * Pur. Le test du matériel lui-même passe par les view-models de l'entrée de l'élève, sans copie
 * (`deviceCheckViewModel`, `liveEntryEquipment`, `liveEntryJoin` : leurs mots valent pour le masteur, et
 * `liveEntryJoin` dit aussi quand l'entrée est « sans vérifier ») ; ici, seulement ce qui change avec lui :
 *  - la barre : la salle (l'ancien en-tête disait « LIVE - {salle} »), puis l'heure et ce que fait l'écran ;
 *  - l'heure de la séance (`liveStartStatus`, la phrase de l'élève : « Ton live commence à 17h00 ») ;
 *  - les élèves déjà là, par leur prénom : le masteur les connaît.
 */
import { type TutorPreCallModel } from "../../../adapters/tutor/PreCall/tutorPreCallAdapter"
import { formatHour } from "../../../ui/formatters/dateFormatter"
import { capitalizeFirst, joinFrench, numberInWords } from "../../../ui/formatters/textFormatter"
import { liveStartStatus } from "../../student/LiveEntry/liveEntryLobbyViewModel"

const TEXTS = {
    room: "Ton live",
    doing: "Vérifie ton matériel avant d'entrer",
    liveAt: (hour: string) => `Live de ${hour}`,
    oneHere: (who: string) => `${who} est déjà là`,
    manyHere: (who: string) => `${who} sont déjà là`,
}

const SEPARATOR = " · "

/** La barre : « Cours partagé · Maths 4ᵉ », puis « Live de 17h00 · Vérifie ton matériel avant d'entrer ». */
export function tutorPreCallHeading(model: Pick<TutorPreCallModel, "roomName" | "date">): {
    title: string
    caption: string
} {
    return {
        title: model.roomName !== "" ? model.roomName : TEXTS.room,
        caption: model.date != null ? `${TEXTS.liveAt(formatHour(model.date))}${SEPARATOR}${TEXTS.doing}` : TEXTS.doing,
    }
}

/** L'heure de la séance du jour : pas encore venue, ou passée. Rien sans séance ni un autre jour. */
export function tutorPreCallStatus(
    model: Pick<TutorPreCallModel, "date" | "duration">,
    now: number = Date.now()
): { text: string; detail?: string } | undefined {
    if (model.date == null) return undefined
    return liveStartStatus({ date: model.date, duration: model.duration, statuses: [] }, now)
}

/**
 * « Jules est déjà là », « Jules et Léa sont déjà là », « Jules, Léa et Karim sont déjà là ». Un élève sans
 * prénom connu : on dit combien (« Deux élèves sont déjà là »).
 */
export function tutorPresentLabel(present: { name: string }[]): string | undefined {
    if (present.length === 0) return undefined
    const names = present.map((student) => student.name.trim())
    const who = names.some((name) => name === "")
        ? capitalizeFirst(`${numberInWords(present.length)} ${present.length === 1 ? "élève" : "élèves"}`)
        : joinFrench(names)
    return present.length === 1 ? TEXTS.oneHere(who) : TEXTS.manyHere(who)
}
